import { scaleTextError } from "./constants";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function totalWeightage(items = []) {
  return Math.round(items.reduce((sum, item) => sum + (Number(item.weightage) || 0), 0) * 100) / 100;
}

export function validateGoal(goal) {
  const errors = {};
  if (!goal.title?.trim()) errors.title = "Enter a goal title.";
  else if (goal.title.trim().length > 120) errors.title = "Keep the title under 120 characters.";
  if (!goal.description?.trim()) errors.description = "Describe the expected outcome.";
  if (!goal.categoryId) errors.categoryId = "Choose a category.";
  if (!goal.priority) errors.priority = "Choose a priority.";
  const w = Number(goal.weightage);
  if (goal.weightage === "" || goal.weightage === null || goal.weightage === undefined || Number.isNaN(w)) errors.weightage = "Enter a goal percentage.";
  else if (w <= 0 || w > 100) errors.weightage = "Goal percentage must be between 1 and 100.";
  if (goal.target === "" || goal.target === null || goal.target === undefined) errors.target = "Enter a target.";
  // [UPDATED] The measurement scale (from / to) replaces the unit list; only the scale itself is checked.
  const scaleErr = scaleTextError(goal.scale ?? "0 to 5");
  if (scaleErr) errors.scale = scaleErr;
  if (!goal.dueDate) errors.dueDate = "Choose a due date.";
  return errors;
}

export function validateEmployee(employee, allEmployees = [], isEdit = false) {
  const errors = {};
  const id = employee.id?.trim();
  const email = employee.email?.trim().toLowerCase();
  if (!id) errors.id = "Employee ID is required.";
  else if (!isEdit && allEmployees.some((e) => e.id.toLowerCase() === id.toLowerCase())) {
    errors.id = "This Employee ID is already in use.";
  }
  if (!employee.name?.trim()) errors.name = "Employee name is required.";
  if (!email) errors.email = "Official email is required.";
  else if (!EMAIL_RE.test(email)) errors.email = "Enter a valid email address.";
  else if (allEmployees.some((e) => e.email.toLowerCase() === email && e.id !== employee.id)) {
    errors.email = "This email belongs to another employee.";
  }
  if (!employee.departmentId) errors.departmentId = "Choose a department.";
  if (!employee.designationId) errors.designationId = "Choose a designation.";
  if (employee.managerId && employee.managerId === employee.id) {
    errors.managerId = "An employee cannot be their own reporting manager.";
  }
  if (!employee.dateOfJoining) errors.dateOfJoining = "Date of joining is required.";
  if (!isEdit && !employee.role) errors.role = "Choose an application role.";
  // [NEW] migration 010 HR attributes (all optional). Limits match the backend schema.
  const personalEmail = String(employee.personalEmail || "").trim();
  if (personalEmail && !EMAIL_RE.test(personalEmail)) errors.personalEmail = "Enter a valid personal email address.";
  if (employee.dateOfBirth) {
    const today = new Date().toISOString().slice(0, 10);
    if (employee.dateOfBirth >= today) errors.dateOfBirth = "Date of birth must be in the past.";
    else if (employee.dateOfJoining && employee.dateOfBirth >= employee.dateOfJoining) errors.dateOfBirth = "Date of birth must be before the date of joining.";
  }
  const limits = { gender: 50, mobilePhone: 50, personalEmail: 255, currentAddress: 500, legalEntity: 200, businessUnit: 200, costCenter: 100, primarySkill: 500, professionalExperience: 100 };
  Object.entries(limits).forEach(([key, max]) => {
    if (!errors[key] && String(employee[key] || "").trim().length > max) errors[key] = `Use at most ${max} characters.`;
  });
  return errors;
}

export function validateCycle(cycle, options = {}) {
  const errors = {};
  if (!cycle.name?.trim()) errors.name = "Enter a cycle name, for example Oct 2026 - Mar 2027.";
  if (!cycle.startDate) errors.startDate = "Choose a start date.";
  if (!cycle.endDate) errors.endDate = "Choose an end date.";
  if (cycle.startDate && cycle.endDate && cycle.endDate <= cycle.startDate) {
    errors.endDate = "End date must be after the start date.";
  }
  (cycle.stages || []).forEach((stage) => {
    if (!stage.startDate || !stage.endDate) {
      errors[`stage_${stage.key}`] = "Set both dates.";
    } else if (stage.endDate < stage.startDate) {
      errors[`stage_${stage.key}`] = "End date is before start date.";
    } else if (cycle.startDate && cycle.endDate && (stage.startDate < cycle.startDate || stage.endDate > cycle.endDate)) {
      errors[`stage_${stage.key}`] = "Window must fall inside the cycle dates.";
    } else if (!options.allowPast && stage.endDate < todayLocal()) {
      errors[`stage_${stage.key}`] = "This window has already ended. Pick dates from today onwards.";
    }
  });
  const byKey = Object.fromEntries((cycle.stages || []).map((s) => [s.key, s]));
  const order = [["goalSetting", "Goal setting"], ["selfAssessment", "Self assessment"], ["managerReview", "Manager review"], ["hrFinalization", "HR finalization"]];
  for (let i = 1; i < order.length; i += 1) {
    const [prevKey, prevLabel] = order[i - 1];
    const [key] = order[i];
    const prev = byKey[prevKey];
    const cur = byKey[key];
    if (prev?.startDate && cur?.startDate && !errors[`stage_${key}`]) {
      if (cur.startDate < prev.startDate) errors[`stage_${key}`] = `Can't open before ${prevLabel} opens (${prev.startDate}).`;
      else if (cur.endDate && prev.endDate && cur.endDate < prev.endDate) errors[`stage_${key}`] = `Can't close before ${prevLabel} closes (${prev.endDate}).`;
    }
  }
  return errors;
}

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const hasErrors = (errors) => Object.keys(errors).length > 0;
